import { useState } from 'react';
import { X, Upload, Download, FileSpreadsheet } from 'lucide-react';
import Papa from 'papaparse';
import LoadingSpinner from '../LoadingSpinner';
import { bulkUploadStudents } from '../../api/services/studentsService';


function UploadCSVModal({ isOpen, onClose, onSuccess }) {
  const [csvFile, setCsvFile] = useState(null);
  const [csvData, setCsvData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [csvError, setCsvError] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);

  // ── Parse CSV using PapaParse ──
  const parseCSV = (file) => {
    return new Promise((resolve, reject) => {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        encoding: 'UTF-8',
        beforeFirstChunk: (chunk) => chunk.replace(/^\uFEFF/, ''),
        transformHeader: (header) => {
          const h = header.trim().toLowerCase().replace(/\s+/g, '_');
          const headerMap = {
            'name': 'name',
            'student_name': 'name',
            'firstname': 'first_name',
            'lastname': 'last_name',
            'department': 'branch',
            'degree':'degree',
            'dept': 'branch',
            'major': 'branch',
            'year_of_graduation': 'year',
            'graduation_year': 'year',
            'current_semester': 'semester',
            'sem': 'semester',
            'id': 'student_id',
            'roll_number': 'student_id',
            'phone': 'phone_number',
            'mobile': 'phone_number',
            'mobile_number': 'phone_number',
          };
          const mapped = headerMap[h] || h;
          console.log(`Header: "${header}" → "${h}" → "${mapped}"`);
          return mapped;
        },
        complete: (results) => {
          console.log('Final headers:', results.meta.fields);
          console.log('First row:', results.data[0]);
  
          if (results.errors.length > 0) {
            reject(new Error(`CSV parsing error: ${results.errors[0].message}`));
            return;
          }
  
          const requiredHeaders = ['email', 'name'];
          const headers = results.meta.fields;
          const missing = requiredHeaders.filter(h => !headers.includes(h));
          if (missing.length > 0) {
            reject(new Error(`Missing required columns: ${missing.join(', ')}`));
            return;
          }
  
          const students = results.data
            .filter(row => row.email && row.name)
            .map((row, index) => ({
              email: row.email?.trim() || '',
              name: row.name?.trim() || '',
              phone_number: row.phone_number?.trim() || '',
              student_id: row.student_id?.trim() || row.email?.trim().split('@')[0]?.toUpperCase() || `STU-${index + 1}`,
              degree: row.degree?.trim() || '',
              branch: row.branch?.trim() || '',
              year: row.year?.trim() || '',
              semester: row.semester?.trim() || '',
            }));
  
          if (students.length === 0) {
            reject(new Error('No valid student records found in CSV'));
            return;
          }
  
          resolve(students);
        },
        error: (error) => {
          reject(new Error(error.message || 'Failed to parse CSV'));
        },
      });
    });
  };


  // ── File handlers ──
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      setCsvFile(file); setCsvError(null); setUploadResult(null);
      try { setCsvData(await parseCSV(file)); }
      catch (error) { setCsvError(error.message); setCsvData(null); }
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file && (file.type === 'text/csv' || file.name.endsWith('.csv'))) {
      setCsvFile(file); setCsvError(null); setUploadResult(null);
      try { setCsvData(await parseCSV(file)); }
      catch (error) { setCsvError(error.message); setCsvData(null); }
    }
  };

  // ── Download sample template ──
  const downloadSampleTemplate = () => {
    const csv = [
      'email,first_name,last_name,phone_number,student_id,branch,year,semester',
      'student1@gla.ac.in,John,Doe,9998887777,CS-001,Computer Science,2027,6th',
      'student2@gla.ac.in,Jane,Smith,9998886666,EC-002,Electronics,2026,4th',
      'student3@gla.ac.in,Bob,Johnson,9998885555,ME-003,Mechanical,2028,2nd',
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'student_template.csv'; a.click();
    window.URL.revokeObjectURL(url);
  };

  // ── Submit to API ──
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!csvFile || !csvData || csvData.length === 0) {
      setCsvError('Please upload a CSV file with valid student data');
      return;
    }
  
    setIsSubmitting(true);
    setCsvError(null);
  
    try {
      const response = await bulkUploadStudents(csvData);
      if (response.success) {
        setUploadResult(response.data);
        setTimeout(() => { onSuccess(); handleClose(); }, 2000);
      } else {
        setCsvError(response.data?.message || 'Failed to upload students');
      }
    } catch (error) {
      console.error('Error uploading students:', error);
      setCsvError(error.message || 'Network error. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // ── Close & reset ──
  const handleClose = () => {
    setCsvFile(null); setCsvData(null); setCsvError(null); setUploadResult(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/60 z-[100] backdrop-blur-sm" onClick={handleClose} />

      {/* Modal */}
      <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
        <div className="bg-screen-bg rounded-lg w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col border border-purple-glow-30 shadow-glow-lg"
          onClick={(e) => e.stopPropagation()}>

          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-purple-glow-15">
            <h2 className="text-2xl font-bold text-text-main">Upload Students CSV</h2>
            <button onClick={handleClose} className="p-2 rounded-lg transition-colors cursor-pointer">
              <X size={20} className="text-btn-text" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {uploadResult ? (
              /* ── Success State ── */
              <div className="text-center py-8">
                <div className="mb-4 text-green-600">
                  <FileSpreadsheet size={48} className="mx-auto" />
                </div>
                <h3 className="text-xl font-semibold text-text-main mb-2">Upload Successful!</h3>
                <p className="text-text-sub mb-2">{uploadResult.message}</p>
                {uploadResult.errors.length > 0 && (
                  <div className="mt-4">
                    <p className="text-sm text-red-500 mb-2">{uploadResult.errors.length} error(s) occurred:</p>
                    <div className="max-h-32 overflow-y-auto text-left">
                      {uploadResult.errors.map((err, i) => (
                        <p key={i} className="text-xs text-red-400">
                          Row {err.row || i + 1}: {err.message || err.error || JSON.stringify(err)}
                        </p>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : (
              /* ── Upload Form ── */
              <>
                <div className="mb-6">
                  <label className="block text-sm font-semibold text-text-main mb-2">
                    Upload Students CSV
                  </label>

                  {/* Drop zone */}
                  <div
                    onDrop={handleDrop}
                    onDragOver={(e) => e.preventDefault()}
                    className="border-2 border-dashed border-purple-glow-30 rounded-lg p-8 text-center hover:border-primary transition-colors cursor-pointer"
                    onClick={() => document.getElementById('csv-upload').click()}
                  >
                    <input
                      id="csv-upload"
                      type="file"
                      accept=".csv"
                      onChange={handleFileChange}
                      className="hidden"
                    />

                    {csvFile ? (
                      <div className="flex flex-col items-center gap-2">
                        <FileSpreadsheet size={32} className="text-primary" />
                        <p className="text-sm font-medium text-text-main">{csvFile.name}</p>
                        {csvData && (
                          <p className="text-xs text-green-600">
                            {csvData.length} student{csvData.length !== 1 ? 's' : ''} found
                          </p>
                        )}
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setCsvFile(null); setCsvData(null); setCsvError(null); }}
                          className="flex flex-row gap-2 text-xs bg-red-600 text-main  mt-1 cursor-pointer"
                        >
                          <X size={16}/>
                          Remove file
                        </button>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center gap-2">
                        <Upload size={32} className="text-text-sub" />
                        <p className="text-sm font-medium text-text-main">Click to upload or drag & drop</p>
                        <p className="text-xs text-text-sub">
                          CSV with columns: email, first_name, last_name, phone_number, student_id, branch, year, semester
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Error */}
                  {csvError && <p className="text-red-500 text-sm mt-2">{csvError}</p>}

                  {/* Template download */}
                  <button
                    type="button"
                    onClick={downloadSampleTemplate}
                    className="mt-2 text-sm text-btn-text flex items-center gap-1 cursor-pointer"
                  >
                    <Download size={14} />
                    <span>Download sample template</span>
                  </button>
                </div>

                {/* Preview parsed data */}
                {csvData && csvData.length > 0 && (
                  <div className="mb-4">
                    <h4 className="text-sm font-semibold text-text-main mb-2">
                      Preview ({csvData.length} students)
                    </h4>
                    <div className="max-h-48 overflow-auto border border-purple-glow-20 rounded-lg">
                      <table className="w-full text-xs">
                        <thead className="bg-purple-500 text-text-primary sticky top-0">
                          <tr>
                            {['#', 'Email', 'Name', 'Student ID', 'Branch', 'Degree', 'Year', 'Semester'].map(h => (
                              <th key={h} className="px-3 py-2 text-left text-text-primary font-semibold whitespace-nowrap">{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-purple-glow-10">
                          {csvData.slice(0, 10).map((s, i) => (
                            <tr key={i} className="hover:bg-purple-glow-5">
                              <td className="px-3 py-2 text-text-sub">{i + 1}</td>
                              <td className="px-3 py-2 text-text-main">{s.email}</td>
                              <td className="px-3 py-2 text-text-main">{s.name}</td>
                              <td className="px-3 py-2 text-text-main">{s.student_id}</td>
                              <td className="px-3 py-2 text-text-sub">{s.branch}</td>
                              <td className="px-3 py-2 text-text-sub">{s.degree}</td>
                              <td className="px-3 py-2 text-text-sub">{s.year}</td>
                              <td className="px-3 py-2 text-text-sub">{s.semester}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {csvData.length > 10 && (
                        <p className="text-xs text-text-sub text-center py-2">
                          ...and {csvData.length - 10} more
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Footer */}
          {!uploadResult && (
            <div className="flex gap-3 justify-end p-6 border-t border-purple-glow-15">
              <button
                type="button"
                onClick={handleClose}
                className="px-6 py-2 rounded-lg border border-purple-glow-20 text-btn-text transition-colors cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting || !csvFile || !csvData || csvData.length === 0}
                className="px-6 py-2 rounded-lg text-btn-text font-semibold bg-primary shadow-glow-btn transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <LoadingSpinner size={16} />
                    <span>Uploading {csvData?.length || 0} students...</span>
                  </>
                ) : (
                  `Upload ${csvData?.length || 0} Students`
                )}
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default UploadCSVModal;